import React, { useMemo, useState } from 'react';
import { Box, Button, Divider, Modal, Typography, FormControlLabel, Switch } from '@mui/material';

const formatCount = (label, n) => `${label}: ${n}`;

const summarizeNode = (n) => {
  const label = n?.data?.label || n?.data?.name || '';
  const type = n?.type ? ` (${n.type})` : '';
  return `${n.id}${label ? ` — ${label}` : ''}${type}`;
};

const summarizeEdge = (e) => {
  const label = e?.data?.label ? ` — ${e.data.label}` : '';
  return `${e.id}: ${e.source} → ${e.target}${label}`;
};

export function FlowSaveConfirmModal({ open, onClose, onConfirm, diff }) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const nodes = diff?.nodes;
  const edges = diff?.edges;

  const humanSummary = useMemo(() => {
    const nodeAdded = nodes?.added || [];
    const nodeRemoved = nodes?.removed || [];
    const nodeModified = nodes?.modified || [];

    const edgeAdded = edges?.added || [];
    const edgeRemoved = edges?.removed || [];
    const edgeModified = edges?.modified || [];

    const lines = [];

    if (nodeAdded.length) {
      lines.push(`• Se agregarán ${nodeAdded.length} nodo(s):`);
      nodeAdded.slice(0, 5).forEach((n) => lines.push(`  - ${summarizeNode(n)}`));
      if (nodeAdded.length > 5) lines.push(`  - … y ${nodeAdded.length - 5} más`);
    }

    if (nodeRemoved.length) {
      lines.push(`• Se eliminarán ${nodeRemoved.length} nodo(s):`);
      nodeRemoved.slice(0, 5).forEach((n) => lines.push(`  - ${summarizeNode(n)}`));
      if (nodeRemoved.length > 5) lines.push(`  - … y ${nodeRemoved.length - 5} más`);
    }

    if (nodeModified.length) {
      lines.push(`• Se modificarán ${nodeModified.length} nodo(s):`);
      nodeModified.slice(0, 5).forEach((m) => lines.push(`  - ${summarizeNode(m.after)}`));
      if (nodeModified.length > 5) lines.push(`  - … y ${nodeModified.length - 5} más`);
    }

    if (edgeAdded.length) {
      lines.push(`• Se agregarán ${edgeAdded.length} relación(es):`);
      edgeAdded.slice(0, 5).forEach((e) => lines.push(`  - ${summarizeEdge(e)}`));
      if (edgeAdded.length > 5) lines.push(`  - … y ${edgeAdded.length - 5} más`);
    }

    if (edgeRemoved.length) {
      lines.push(`• Se eliminarán ${edgeRemoved.length} relación(es):`);
      edgeRemoved.slice(0, 5).forEach((e) => lines.push(`  - ${summarizeEdge(e)}`));
      if (edgeRemoved.length > 5) lines.push(`  - … y ${edgeRemoved.length - 5} más`);
    }

    if (edgeModified.length) {
      lines.push(`• Se modificarán ${edgeModified.length} relación(es):`);
      edgeModified.slice(0, 5).forEach((m) => lines.push(`  - ${summarizeEdge(m.after)}`));
      if (edgeModified.length > 5) lines.push(`  - … y ${edgeModified.length - 5} más`);
    }

    if (lines.length === 0) {
      return 'No se detectaron cambios entre la última versión guardada y la actual.';
    }

    return lines.join('\n');
  }, [nodes, edges]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        className="apple-modal"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 720,
          maxWidth: '95vw',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          outline: 'none',
          overflow: 'auto',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Confirmar guardado
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Revisa los cambios detectados antes de guardar.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Resumen
        </Typography>
        <Box
          sx={{
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: 12,
            bgcolor: '#F2F2F7',
            color: '#1D1D1F',
            borderRadius: 1,
            p: 2,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {humanSummary}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Nodos
            </Typography>
            <Typography variant="body2">{formatCount('Agregados', nodes?.added?.length || 0)}</Typography>
            <Typography variant="body2">{formatCount('Eliminados', nodes?.removed?.length || 0)}</Typography>
            <Typography variant="body2">{formatCount('Modificados', nodes?.modified?.length || 0)}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Relaciones
            </Typography>
            <Typography variant="body2">{formatCount('Agregadas', edges?.added?.length || 0)}</Typography>
            <Typography variant="body2">{formatCount('Eliminadas', edges?.removed?.length || 0)}</Typography>
            <Typography variant="body2">{formatCount('Modificadas', edges?.modified?.length || 0)}</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Switch checked={showAdvanced} onChange={(e) => setShowAdvanced(e.target.checked)} />}
            label="Resumen avanzado"
          />
        </Box>

        {showAdvanced ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Detalle (JSON)
            </Typography>
            <Box
              sx={{
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: 12,
                bgcolor: '#0b0f19',
                color: '#e6edf3',
                borderRadius: 1,
                p: 2,
                maxHeight: 320,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(diff || {}, null, 2)}
            </Box>
          </>
        ) : null}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            Volver atrás
          </Button>
          <Button variant="contained" onClick={onConfirm}>
            Sí, guardar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
